"use client";

import { VideoCard } from "./VideoCard";
import { Skeleton } from "@/components/ui/skeleton";
import type { VideoResponse } from "@/types/api";
import { useTranslation } from "@/hooks/useTranslation";

interface VideoGridProps {
  videos: VideoResponse[];
  isLoading?: boolean;
}

export function VideoGrid({ videos, isLoading }: VideoGridProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <VideoCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 rounded-full bg-muted p-4">
          <svg
            className="h-8 w-8 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        </div>
        <h3 className="mb-1 font-medium">{t("grid.empty")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("grid.emptyHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {videos.map((video, i) => (
        <VideoCard key={video.id} video={video} data-tour={i === 0 ? "video-card" : undefined} />
      ))}
    </div>
  );
}

function VideoCardSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="aspect-[9/16] rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}
